// 계산 원장이 찍을 입력과 산출 — **사람이 정본과 한 자리씩 대조하는 데이터**
//
// 화면(.tsx)에서 이 데이터를 떼어 낸 이유가 하나 있다. 원장은 코어를 부르고 코어가 낸
// 값을 그대로 찍는 구조라, **화면이 들고 있는 입력이 정본에서 떨어져 나가면 계산은
// 멀쩡한데 화면만 조용히 다른 이야기를 한다.** 그리고 그 화면이 사용자가 정본과 대조
// 하는 유일한 창이다 — 창이 틀어져 있으면 대조 자체가 무의미해진다. 그래서 데이터를
// 여기 두고 tests/unit/timing/ledger.test.ts 가 못박는다.
//
// **시각이 고정값이다.** 흐르는 시계 위에 올리면 숫자가 매 초 바뀌어 정본의 고정 숫자와
// 대조할 수 없다. 목업 데이터가 아니라 정본과 판정 계약이 검증 데이터로 지정한 값이다.
//
// 출처:
//   공격 — 정본 「시퀀스와 offset」 절의 반례 블록과 그 거울 케이스
//   수비 — 판정 계약 「적 rally 등록」·「보낼 시점 계산」·「착탄 미상」 세 케이스
//   실측 marching — 인게임에서 한 번씩 재어 모은 값 (구현 계획서 roster 명부 절)

import {
  between,
  type Duration,
  durationFromSeconds,
  type Instant,
  instant,
  toMillis,
} from '../../../core/time/brand.ts'
import type { TriggerPhase } from '../../../core/timing/defense.ts'
import { DEFAULT_LANG, defense as defenseStrings } from '../../../i18n/index.ts'
import {
  byImpact,
  checkHandleInput,
  type FloorContext,
  compareByImpact,
  enemyImpact,
  formatCountdown,
  formatDuration,
  formatImpactClock,
  formatSignedOffset,
  marchingAnchor,
  planSquad,
  previewRallyingOptions,
  rallyingAnchor,
  shiftToFloor,
  solveOnImpactAxis,
  solveOnOpenAxis,
  type EnemyAnchor,
  type RallyDraft,
} from '../../../core/timing/index.ts'

const DAY = Date.UTC(2026, 7, 11)
const at = (hh: number, mm: number, ss: number): Instant =>
  instant(DAY + ((hh * 60 + mm) * 60 + ss) * 1000)
const secs = (value: number): Duration => durationFromSeconds(value)

// ── 공격: 정본의 반례와 거울 케이스 ────────────────────────────────────────

const IMPACT_MAIN = at(12, 7, 0)
const ATTACK_NOW_AT = at(11, 58, 55)

const MAIN: RallyDraft = {
  id: 'main',
  kind: 'main',
  rallying: secs(300),
  marching: secs(120),
  offset: secs(0),
}
const STRIKE: RallyDraft = {
  id: 'strike',
  kind: 'strike',
  rallying: secs(300),
  marching: secs(180),
  offset: secs(-1),
}
const GARRISON: RallyDraft = {
  id: 'garrison',
  kind: 'garrison',
  rallying: secs(300),
  marching: secs(60),
  offset: secs(2),
}

const ATTACK_DRAFTS = [MAIN, STRIKE, GARRISON]

export interface AttackRow {
  id: string
  label: string
  note: string
  rallying: string
  marching: string
  offset: string
  impactAxis: { open: string; impact: string }
  openAxis: { open: string; impact: string }
}

const LABELS: Record<string, { label: string; note: string }> = {
  main: { label: 'main', note: '시퀀스의 중심. 착탄축의 원점이라 간격이 0 이다' },
  strike: { label: 'strike', note: '행군이 main 보다 1분 길다 — 정본의 반례' },
  garrison: { label: 'garrison', note: '행군이 main 보다 1분 짧다 — 거울 케이스' },
}

const impactAxisSolved = solveOnImpactAxis(ATTACK_DRAFTS, IMPACT_MAIN)
const openAxisSolved = solveOnOpenAxis(ATTACK_DRAFTS, IMPACT_MAIN)

export const attackRows: AttackRow[] = ATTACK_DRAFTS.map((draft) => {
  const right = impactAxisSolved.find((rally) => rally.id === draft.id)
  const wrong = openAxisSolved.find((rally) => rally.id === draft.id)
  if (right === undefined || wrong === undefined) throw new Error(`풀이에 ${draft.id} 가 없다`)
  const meta = LABELS[draft.id] ?? { label: draft.id, note: '' }
  return {
    id: draft.id,
    label: meta.label,
    note: meta.note,
    rallying: formatDuration(draft.rallying),
    marching: formatDuration(draft.marching),
    offset: formatSignedOffset(draft.offset),
    impactAxis: { open: formatImpactClock(right.open), impact: formatImpactClock(right.impact) },
    openAxis: { open: formatImpactClock(wrong.open), impact: formatImpactClock(wrong.impact) },
  }
})

/** 두 축의 착탄 순서. **완전히 뒤집힌다** — 그것이 이 표의 결론이다. */
export const attackOrder = {
  impactAxis: [...impactAxisSolved].sort(byImpact).map((rally) => rally.id),
  openAxis: [...openAxisSolved].sort(byImpact).map((rally) => rally.id),
}

export const ATTACK_NOW = formatImpactClock(ATTACK_NOW_AT)

// ── 개시 하한: 자동 평행이동과 직접 입력 차단은 다른 일이다 ────────────────

export interface FloorRow {
  label: string
  earliestOpen: string
  floor: string
  shift: string
  applied: boolean
}

function floorRow(label: string, drafts: RallyDraft[]): FloorRow {
  const solved = solveOnImpactAxis(drafts, IMPACT_MAIN)
  const shifted = shiftToFloor(solved, ATTACK_NOW_AT)
  const earliest = Math.min(...solved.map((rally) => toMillis(rally.open)))
  return {
    label,
    earliestOpen: formatImpactClock(instant(earliest)),
    floor: formatImpactClock(instant(toMillis(ATTACK_NOW_AT) + 6_000)),
    shift: formatDuration(shifted.shift),
    applied: toMillis(shifted.shift) > 0,
  }
}

export const floorRows: FloorRow[] = [
  floorRow('main 하나뿐', [MAIN]),
  floorRow('strike · garrison 을 붙인 뒤', ATTACK_DRAFTS),
]

export interface HandleCheckRow {
  input: string
  context: FloorContext
  accepted: boolean
  /** 거절의 **이름**이다. 문장이 아니다. 문장은 화면이 이 이름으로 만든다. */
  code: string
}

/**
 * 하한 판정 셋. **보정이 아니라 차단이다.**
 *
 * 같은 5초가 문맥에 따라 다른 이름으로 거절되는 것이 이 표의 요점이다. 사람이 손잡이에
 * 친 값은 되돌려 주면 되지만, 계산으로 정해진 개시 여유는 사람이 친 적이 없어 「넣은
 * 값」이라 부르면 거짓이 된다.
 */
export const handleChecks: HandleCheckRow[] = (
  [
    [secs(5), 'handle'],
    [secs(6), 'handle'],
    [secs(5), 'derived'],
  ] as const
).map(([handle, context]) => {
  const verdict = checkHandleInput(handle, context)
  return {
    input: formatDuration(handle),
    context,
    accepted: verdict.accepted,
    code: verdict.accepted ? '' : verdict.code,
  }
})

// ── 대기 시간 네 옵션 ──────────────────────────────────────────────────────

export interface OptionRow {
  rallying: string
  open: string
  lead: string
  feasible: boolean
  minimumOffset: string
}

// 이 절만 기준 시각이 다른 이유: 진행 중 시퀀스에 rally 를 **나중에** 붙이는 상황이라
// 손잡이를 잡던 시각(11:58:55)보다 뒤여야 한다. 그래서 화면이 이 값을 따로 적는다.
const OPTIONS_NOW_AT = at(12, 0, 0)
export const OPTIONS_NOW = formatImpactClock(OPTIONS_NOW_AT)

export const optionRows: OptionRow[] = previewRallyingOptions({
  impactMain: IMPACT_MAIN,
  offset: secs(30),
  marching: secs(120),
  now: OPTIONS_NOW_AT,
}).map((option) => ({
  rallying: formatDuration(option.rallying),
  open: formatImpactClock(option.open),
  lead: formatDuration(option.lead),
  feasible: option.feasible,
  minimumOffset: formatSignedOffset(option.minimumOffset),
}))

// ── 수비: 착탄 역산이 둘로 갈렸다 ──────────────────────────────────────────

const T0 = at(20, 0, 0)
const after = (seconds: number): Instant => instant(toMillis(T0) + Math.round(seconds * 1000))
const DEFENSE_NOW_AT = after(25)

interface EnemyCase {
  id: string
  roster: string
  anchor: EnemyAnchor
  /** 명부의 누적 marching. 아직 못 모은 roster 는 undefined. */
  rosterMarching: Duration | undefined
}

const ENEMY_CASES: EnemyCase[] = [
  {
    id: 'kael-1',
    roster: 'Kael',
    anchor: rallyingAnchor(after(0), secs(60)),
    rosterMarching: secs(120),
  },
  {
    id: 'nyx-1',
    roster: 'Nyx',
    anchor: rallyingAnchor(after(10), secs(120)),
    rosterMarching: secs(100),
  },
  {
    id: 'vex-1',
    roster: 'Vex',
    anchor: rallyingAnchor(after(20), secs(80)),
    rosterMarching: secs(150),
  },
  {
    // 이미 행군에 들어간 Kael 의 두 번째 rally. **명부의 2:00 이 계산에 쓰이지 않는다.**
    id: 'kael-2',
    roster: 'Kael',
    anchor: marchingAnchor(after(30), secs(40)),
    rosterMarching: secs(120),
  },
  {
    // marching 을 아직 못 모은 적. rallying 앵커라 착탄이 미상이다.
    id: 'moro-1',
    roster: 'Moro',
    anchor: rallyingAnchor(after(15), secs(100)),
    rosterMarching: undefined,
  },
  {
    // 같은 roster 인데 행군 앵커라 **명부가 비어 있어도 착탄이 확정된다.**
    // 「착탄 미상」을 우회하는 유일한 경로가 이 행이다.
    id: 'moro-2',
    roster: 'Moro',
    anchor: marchingAnchor(after(35), secs(50)),
    rosterMarching: undefined,
  },
]

export interface EnemyRow {
  id: string
  roster: string
  phase: 'rallying' | 'marching'
  phaseLabel: string
  anchorAt: string
  remaining: string
  rosterMarching: string
  /** 이 행의 착탄 계산이 명부의 marching 을 실제로 읽었는가. */
  usesRosterMarching: boolean
  impact: string | null
  /** 앵커로부터 착탄까지. 정본과 대조할 값이 이것이다. */
  fromAnchor: string | null
  /** 정렬 대조용 — rallying 이 끝나는 순간의 밀리초. */
  rallyingEndsAtMillis: number
}

// 정렬 키(Instant)와 표시용 행을 나란히 들고 다닌다. 행 안에 키를 섞어 넣었다가 빼면
// 행의 모양이 잠시 달라져 타입이 그것을 못 지킨다.
export const enemyRows: EnemyRow[] = ENEMY_CASES.map((item) => {
  const impact = enemyImpact(item.anchor, item.rosterMarching)
  return {
    impact,
    row: {
      id: item.id,
      roster: item.roster,
      phase: item.anchor.phase,
      phaseLabel: item.anchor.phase === 'rallying' ? 'rallying 앵커' : '행군 앵커',
      anchorAt: formatImpactClock(item.anchor.at),
      remaining: formatDuration(item.anchor.remaining),
      rosterMarching:
        item.rosterMarching === undefined ? '없음' : formatDuration(item.rosterMarching),
      // **행군 앵커는 명부를 읽지 않는다.** 이 표에서 사람이 볼 것이 그것이다.
      usesRosterMarching: item.anchor.phase === 'rallying',
      impact: impact === undefined ? null : formatImpactClock(impact),
      fromAnchor: impact === undefined ? null : formatDuration(between(item.anchor.at, impact)),
      rallyingEndsAtMillis:
        toMillis(item.anchor.at) +
        (item.anchor.phase === 'rallying' ? toMillis(item.anchor.remaining) : 0),
    },
  }
})
  .sort(compareByImpact)
  .map((entry) => entry.row)

// ── 보낼 시점: R · M · 불가 · 미상 네 갈래 ─────────────────────────────────

interface AllyCase {
  name: string
  marching: Duration
}

const ALLY_CASES: AllyCase[] = [
  { name: '이영희', marching: secs(202.5) },
  { name: '박민수', marching: secs(22.5) },
  { name: '홍길동', marching: secs(240) },
]

export interface SquadCell {
  enemyId: string
  enemyRoster: string
  status: 'ready' | 'impossible' | 'impact-unknown'
  /** `R 1:42.0` 처럼 단계 칩과 값이 붙은 글자. 미상이면 null. */
  trigger: string | null
  countdown: string | null
  lateBy: string | null
}

export interface SquadRow {
  ally: string
  marching: string
  cells: SquadCell[]
}

export const squadRows: SquadRow[] = ALLY_CASES.map((ally) => ({
  ally: ally.name,
  marching: formatDuration(ally.marching),
  cells: ENEMY_CASES.map((enemy): SquadCell => {
    const plan = planSquad({
      anchor: enemy.anchor,
      enemyMarching: enemy.rosterMarching,
      myMarching: ally.marching,
      now: DEFENSE_NOW_AT,
    })
    const base = { enemyId: enemy.id, enemyRoster: enemy.roster }
    if (plan.status === 'impact-unknown') {
      return { ...base, status: plan.status, trigger: null, countdown: null, lateBy: null }
    }
    if (plan.status === 'impossible') {
      return {
        ...base,
        status: plan.status,
        trigger: null,
        countdown: null,
        lateBy: formatDuration(plan.lateBy),
      }
    }
    return {
      ...base,
      status: plan.status,
      trigger: `${triggerLabel(plan.trigger.phase)} ${formatDuration(plan.trigger.remaining)}`,
      countdown: formatCountdown(plan.countdown),
      lateBy: null,
    }
  }),
}))

export const DEFENSE_NOW = formatImpactClock(DEFENSE_NOW_AT)

/**
 * 트리거 단계의 글자. **이 페이지만 한국어에 고정한다.**
 *
 * 코어가 `'R'`·`'M'` 대신 뜻(`'rallying'`·`'marching'`)을 내게 되면서 글자를 고르는 일이
 * 화면으로 내려왔다. 열람 화면은 고른 언어를 따르지만 **이 페이지는 따르지 않는다** —
 * 여기는 사람이 쓰는 화면이 아니라 **판정 계약의 예시 숫자를 그대로 재현해 보이는 감사
 * 문서**이고, 계약이 그 자리에 적어 둔 글자가 `R 1:42.0` 이다. 언어를 따라 흔들리면 이
 * 페이지가 대조 대상이기를 그만둔다.
 *
 * 표를 따로 적지 않고 같은 카탈로그를 읽는 이유는 두 벌이 되면 갈리기 때문이다. 고정하는
 * 것은 **언어**이지 값이 아니다.
 */
function triggerLabel(phase: TriggerPhase): string {
  const t = defenseStrings(DEFAULT_LANG)
  return phase === 'rallying' ? t.triggerRallying : t.triggerMarching
}
